import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch, type Tone } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action, download } from '../lib/api.ts';
import { dateTime, day, since } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { Table } from './Reports.tsx';
import { AppDialog, AppsTab, type AppRow } from './DeveloperApps.tsx';

/**
 * Developer: what another program needs to work with ERP PRO. API keys that act as the person who
 * made them (read only, or read and write, for the parts chosen), webhooks that send events to the
 * business's own systems, and the reference of every record and action this person can use.
 */

type Access = 'read' | 'write';
interface KeyRow {
  id: string;
  name: string;
  prefix: string;
  access: Access;
  modules: string[] | null;
  expires_on: string | null;
  last_used_at: string | null;
  last_ip: string | null;
  revoked_at: string | null;
  user_name: string | null;
  created_at: string;
}
interface HookRow {
  id: string;
  name: string;
  url: string;
  events: string[];
  active: boolean;
  paused_reason: 'failing' | 'by_hand' | null;
  failures: number;
  last_ok_at: string | null;
  last_error_at: string | null;
  user_name: string | null;
  waiting: number;
  delivered_day: number;
  dead_day: number;
}
interface EventDef {
  event: string;
  model: string;
  id: string;
  module: string;
}
interface Overview {
  slug: string;
  today: string;
  keys: KeyRow[];
  webhooks: HookRow[];
  apps: AppRow[];
  modules: { name: string; label: string }[];
  events: EventDef[];
  can: { manage: boolean; webhooks: boolean };
}
interface Delivery {
  id: string;
  event: string;
  event_id: string;
  payload: Record<string, unknown>;
  state: 'pending' | 'delivered' | 'dead';
  attempts: number;
  next_at: string;
  last_status: number | null;
  last_answer: string | null;
  ms: number | null;
  done_at: string | null;
  created_at: string;
}
interface JsonSchema {
  type?: string | string[];
  properties?: Record<string, JsonSchema>;
  required?: string[];
  items?: JsonSchema;
  enum?: unknown[];
  format?: string;
  anyOf?: JsonSchema[];
  minimum?: number;
  maximum?: number;
  maxLength?: number;
  default?: unknown;
}
interface Reference {
  modules: { name: string; label: string }[];
  models: { name: string; label: string; module: string; write: boolean; fields: { name: string; type: string; required: boolean; ref?: string; values?: string[] }[] }[];
  actions: { name: string; module: string; permission: string | null; needs: string | null; upload: boolean; input: JsonSchema }[];
  events: EventDef[];
}

export const origin = () => (typeof window !== 'undefined' ? window.location.origin : '');
/** A module's name in the reader's language, or the server's English one. */
export const moduleName = (name: string, fallback?: string) => {
  for (const key of [`platform.module_${name}`, `dev.mod_${name}`]) {
    const v = t(key as MessageKey);
    if (v !== key) return v;
  }
  return fallback ?? name;
};
const eventLabel = (e: string) => {
  const key = `dev.ev_${e.replace(/\./g, '_')}` as MessageKey;
  const v = t(key);
  return v === key ? e : v;
};

export function CopyButton({ text, label }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  return (
    <Button
      size="sm"
      variant="ghost"
      icon={done ? 'check' : 'copy'}
      onClick={() => {
        void navigator.clipboard?.writeText(text);
        setDone(true);
        setTimeout(() => setDone(false), 1500);
      }}
    >
      {done ? t('dev.copied') : (label ?? t('dev.copy'))}
    </Button>
  );
}

export function Code({ children, copy = true }: { children: string; copy?: boolean }) {
  return (
    <div className="dev-code">
      <pre tabIndex={0}>
        <code>{children}</code>
      </pre>
      {copy ? (
        <span className="dev-code-copy">
          <CopyButton text={children} />
        </span>
      ) : null}
    </div>
  );
}

// ---- the page -------------------------------------------------------------------------------

type Tab = 'start' | 'keys' | 'apps' | 'webhooks' | 'reference';
const TAB_ICON: Record<Tab, IconName> = { start: 'sparkle', keys: 'key', apps: 'apps', webhooks: 'send', reference: 'book' };

export function Developer() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const manage = can('developer.manage');
  const { data: o, reload } = useAction<Overview>('developer.overview');
  const tabs: Tab[] = manage ? ['start', 'keys', 'apps', 'webhooks', 'reference'] : ['start', 'reference'];
  const tab = (tabs.includes(params.get('tab') as Tab) ? params.get('tab') : 'start') as Tab;
  const setTab = (k: Tab) => setParams(k === 'start' ? {} : { tab: k }, { replace: true });
  const [newKey, setNewKey] = useState(false);
  const [editHook, setEditHook] = useState<HookRow | 'new' | null>(null);
  const [editApp, setEditApp] = useState<AppRow | 'new' | null>(null);
  const activeKeys = o?.keys.filter((k) => !k.revoked_at && !(k.expires_on && k.expires_on < o.today)).length ?? 0;
  return (
    <Shell>
      <div className="page reports dev-page">
        <div className="page-head">
          <div>
            <h1>{t('dev.title')}</h1>
            <p className="muted">{t('dev.lead')}</p>
          </div>
          {manage && tab === 'keys' ? (
            <Button variant="primary" icon="plus" onClick={() => setNewKey(true)}>
              {t('dev.new_key')}
            </Button>
          ) : manage && tab === 'apps' ? (
            <Button variant="primary" icon="plus" onClick={() => setEditApp('new')}>
              {t('dev.new_app')}
            </Button>
          ) : manage && tab === 'webhooks' && o?.can.webhooks ? (
            <Button variant="primary" icon="plus" onClick={() => setEditHook('new')}>
              {t('dev.new_webhook')}
            </Button>
          ) : null}
        </div>
        <div className="segmented tabs" role="tablist" aria-label={t('dev.sections')}>
          {tabs.map((k) => (
            <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'active' : ''} onClick={() => setTab(k)}>
              <Icon name={TAB_ICON[k]} size={16} />
              {t(`dev.tab_${k}` as MessageKey)}
              {k === 'keys' && activeKeys ? <span className="dev-tab-count">{activeKeys}</span> : null}
              {k === 'apps' && o?.apps.length ? <span className="dev-tab-count">{o.apps.length}</span> : null}
              {k === 'webhooks' && o?.webhooks.length ? <span className="dev-tab-count">{o.webhooks.length}</span> : null}
            </button>
          ))}
        </div>
        {!o ? (
          <Skeleton h={320} r="var(--radius-lg)" />
        ) : tab === 'start' ? (
          <StartTab o={o} manage={manage} go={setTab} onNewKey={() => setNewKey(true)} />
        ) : tab === 'keys' ? (
          <KeysTab o={o} onNew={() => setNewKey(true)} onChange={reload} />
        ) : tab === 'apps' ? (
          <AppsTab o={o} onNew={() => setEditApp('new')} onEdit={setEditApp} onChange={reload} />
        ) : tab === 'webhooks' ? (
          <WebhooksTab o={o} onNew={() => setEditHook('new')} onEdit={setEditHook} onChange={reload} />
        ) : (
          <ReferenceTab slug={o.slug} />
        )}
        {newKey && o ? (
          <KeyDialog
            o={o}
            onClose={() => setNewKey(false)}
            onMade={async () => {
              await reload();
              if (tab !== 'keys') setTab('keys');
            }}
          />
        ) : null}
        {editApp && o ? <AppDialog modules={o.modules} app={editApp === 'new' ? null : editApp} onClose={() => setEditApp(null)} onSaved={reload} /> : null}
        {editHook && o ? <HookDialog o={o} hook={editHook === 'new' ? null : editHook} onClose={() => setEditHook(null)} onSaved={reload} /> : null}
      </div>
    </Shell>
  );
}

// ---- getting started ----------------------------------------------------------------------------

function StartTab({ o, manage, go, onNewKey }: { o: Overview; manage: boolean; go: (t: Tab) => void; onNewKey: () => void }) {
  const base = origin();
  const key = `erpk.${o.slug}.…`;
  const [lang, setLang] = useState<'curl' | 'js' | 'python'>('curl');
  const samples = {
    curl: `# ${t('dev.sample_list')}
curl ${base}/api/records/invoicing.invoice?state=issued&limit=20 \\
  -H "Authorization: Bearer ${key}"

# ${t('dev.sample_action')}
curl -X POST ${base}/api/actions/invoicing.save_customer \\
  -H "Authorization: Bearer ${key}" \\
  -H "Content-Type: application/json" \\
  -d '{"name":"Alba Soft","is_company":true}'`,
    js: `const ERP = '${base}';
const KEY = process.env.ERP_PRO_KEY; // ${key}

async function erp(path, body) {
  const res = await fetch(ERP + path, {
    method: body ? 'POST' : 'GET',
    headers: { Authorization: 'Bearer ' + KEY, 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error.message);
  return data;
}

// ${t('dev.sample_list')}
const invoices = await erp('/api/records/invoicing.invoice?state=issued&limit=20');`,
    python: `import os, requests

ERP = "${base}"
KEY = os.environ["ERP_PRO_KEY"]  # ${key}
s = requests.Session()
s.headers["Authorization"] = f"Bearer {KEY}"

# ${t('dev.sample_list')}
invoices = s.get(f"{ERP}/api/records/invoicing.invoice", params={"state": "issued", "limit": 20}).json()`,
  };
  const verify = `import { createHmac, timingSafeEqual } from 'node:crypto';

// ${t('dev.sample_verify')}
function verify(secret, header, rawBody) {
  const parts = Object.fromEntries(header.split(',').map((p) => p.split('=')));
  if (Math.abs(Date.now() / 1000 - Number(parts.t)) > 300) return false;
  const want = createHmac('sha256', secret).update(parts.t + '.' + rawBody).digest();
  const got = Buffer.from(parts.v1, 'hex');
  return got.length === want.length && timingSafeEqual(got, want);
}`;
  const steps: { icon: IconName; title: string; body: string; action?: ReactNode }[] = [
    {
      icon: 'key',
      title: t('dev.step1'),
      body: t('dev.step1_body'),
      action: manage ? (
        <Button size="sm" icon="plus" onClick={onNewKey}>
          {t('dev.new_key')}
        </Button>
      ) : undefined,
    },
    {
      icon: 'code',
      title: t('dev.step2'),
      body: t('dev.step2_body'),
      action: (
        <Button size="sm" variant="ghost" icon="book" onClick={() => go('reference')}>
          {t('dev.open_reference')}
        </Button>
      ),
    },
    {
      icon: 'send',
      title: t('dev.step3'),
      body: t('dev.step3_body'),
      action: manage ? (
        <Button size="sm" variant="ghost" icon="send" onClick={() => go('webhooks')}>
          {t('dev.tab_webhooks')}
        </Button>
      ) : undefined,
    },
  ];
  return (
    <div className="dev-start">
      <ol className="dev-steps">
        {steps.map((s, i) => (
          <li key={i} className="panel dev-step">
            <span className="dev-step-n" aria-hidden="true">
              {i + 1}
            </span>
            <div>
              <h2 className="dev-h2">
                <Icon name={s.icon} size={18} /> {s.title}
              </h2>
              <p className="muted small">{s.body}</p>
              {s.action ? <div className="dev-step-act">{s.action}</div> : null}
            </div>
          </li>
        ))}
      </ol>

      <section className="panel" aria-labelledby="dev-base-h">
        <h2 id="dev-base-h" className="dev-h2">
          {t('dev.base')}
        </h2>
        <div className="dev-base">
          <code>{base}</code>
          <CopyButton text={base} />
        </div>
        <ul className="dev-rules small">
          <li>{t('dev.rule_auth')}</li>
          <li>{t('dev.rule_money')}</li>
          <li>{t('dev.rule_errors')}</li>
          <li>{t('dev.rule_rate')}</li>
          <li>{t('dev.rule_lang')}</li>
        </ul>
      </section>

      <section className="panel" aria-labelledby="dev-ex-h">
        <div className="dev-panel-head">
          <h2 id="dev-ex-h" className="dev-h2">
            {t('dev.examples')}
          </h2>
          <div className="segmented subtle" role="tablist" aria-label={t('dev.examples')}>
            {(['curl', 'js', 'python'] as const).map((l) => (
              <button key={l} role="tab" aria-selected={lang === l} className={lang === l ? 'active' : ''} onClick={() => setLang(l)}>
                {l === 'curl' ? 'cURL' : l === 'js' ? 'JavaScript' : 'Python'}
              </button>
            ))}
          </div>
        </div>
        <Code>{samples[lang]}</Code>
      </section>

      <section className="panel" aria-labelledby="dev-wh-h">
        <h2 id="dev-wh-h" className="dev-h2">
          {t('dev.receiving')}
        </h2>
        <p className="muted small">{t('dev.receiving_lead')}</p>
        <Code>{`POST /your/address
ERP-Event: invoicing.invoice.issued
ERP-Delivery: 3f6c…
ERP-Signature: t=1761733200,v1=5d41…

{
  "id": "b7e1…",
  "type": "invoicing.invoice.issued",
  "created_at": "2026-10-29T10:20:00.000Z",
  "business": "${o.slug}",
  "data": { "invoice": "9a0c…", "customer": "41d2…", "total": 1200000 },
  "record": { "type": "invoicing.invoice", "id": "9a0c…", "url": "/api/records/invoicing.invoice/9a0c…" }
}`}</Code>
        <ul className="dev-rules small">
          <li>{t('dev.rule_2xx')}</li>
          <li>{t('dev.rule_retry')}</li>
          <li>{t('dev.rule_dupes')}</li>
        </ul>
        <Code>{verify}</Code>
      </section>
    </div>
  );
}

// ---- API keys -------------------------------------------------------------------------------

function keyState(k: KeyRow, today: string): { tone: Tone; icon: IconName; label: string } {
  if (k.revoked_at) return { tone: 'neutral', icon: 'x', label: t('dev.revoked') };
  if (k.expires_on && k.expires_on < today) return { tone: 'warn', icon: 'clock', label: t('dev.expired') };
  return { tone: 'ok', icon: 'checkCircle', label: t('dev.active') };
}

function KeysTab({ o, onNew, onChange }: { o: Overview; onNew: () => void; onChange: () => Promise<unknown> }) {
  const { busy, run } = useSave();
  const [revoke, setRevoke] = useState<KeyRow | null>(null);
  const [showOld, setShowOld] = useState(false);
  const live = o.keys.filter((k) => !k.revoked_at);
  const old = o.keys.filter((k) => k.revoked_at);
  const rows = showOld ? o.keys : live;
  return (
    <section className="panel">
      <div className="dev-panel-head">
        <div>
          <h2 className="dev-h2">{t('dev.keys')}</h2>
          <p className="muted small">{t('dev.keys_lead')}</p>
        </div>
        {old.length ? (
          <label className="dev-inline-switch small">
            <Switch checked={showOld} onChange={setShowOld} label={t('dev.show_revoked')} />
            {tn('dev.show_revoked_n', old.length)}
          </label>
        ) : null}
      </div>
      {!rows.length ? (
        <EmptyState
          icon="key"
          title={t('dev.no_keys')}
          body={t('dev.no_keys_body')}
          action={
            <Button variant="primary" icon="plus" onClick={onNew}>
              {t('dev.new_key')}
            </Button>
          }
        />
      ) : (
        <Table
          head={[t('dev.key'), t('dev.access'), t('dev.parts'), t('dev.last_used'), t('dev.state'), '']}
          left={[1, 2]}
          phoneHide={[2, 3, 4]}
          rows={rows.map((k) => {
            const st = keyState(k, o.today);
            return {
              key: k.id,
              cells: [
                <span className={`dev-key-cell${k.revoked_at ? ' dev-dim' : ''}`}>
                  <strong>{k.name}</strong>
                  <code className="dev-prefix">
                    erpk.{o.slug}.{k.prefix}…
                  </code>
                  <span className="muted small">
                    {[k.user_name ? t('dev.made_by', { name: k.user_name }) : null, k.expires_on ? t('dev.until', { day: day(k.expires_on) }) : null].filter(Boolean).join(' · ')}
                  </span>
                </span>,
                <AccessBadge access={k.access} />,
                <ModuleChips modules={k.modules} labels={o.modules} />,
                k.last_used_at ? <span title={k.last_ip ?? undefined}>{since(k.last_used_at)}</span> : <span className="muted">{t('dev.never')}</span>,
                <StatusBadge tone={st.tone} icon={st.icon} label={st.label} />,
                k.revoked_at ? <span /> : <IconButton icon="x" label={t('dev.revoke_n', { name: k.name })} onClick={() => setRevoke(k)} />,
              ],
            };
          })}
        />
      )}
      {revoke ? (
        <Confirm
          title={t('dev.revoke_title', { name: revoke.name })}
          body={t('dev.revoke_body')}
          confirm={t('dev.revoke')}
          danger
          busy={busy}
          onClose={() => setRevoke(null)}
          onConfirm={() =>
            void run(async () => {
              await action('developer.revoke_key', { id: revoke.id });
              setRevoke(null);
              await onChange();
            }, t('dev.revoked_done'))
          }
        />
      ) : null}
    </section>
  );
}

export function AccessBadge({ access }: { access: Access }) {
  return access === 'read' ? <StatusBadge tone="info" icon="eye" label={t('dev.read_only')} /> : <StatusBadge tone="warn" icon="edit" label={t('dev.read_write')} />;
}

export function ModuleChips({ modules, labels }: { modules: string[] | null; labels: { name: string; label: string }[] }) {
  if (!modules) return <span className="muted small">{t('dev.all_parts')}</span>;
  const names = modules.map((m) => moduleName(m, labels.find((l) => l.name === m)?.label));
  const shown = names.slice(0, 3);
  return (
    <span className="dev-chips" title={names.join(', ')}>
      {shown.map((n) => (
        <span key={n} className="dev-chip">
          {n}
        </span>
      ))}
      {names.length > shown.length ? <span className="dev-chip dev-chip-more">+{names.length - shown.length}</span> : null}
    </span>
  );
}

function KeyDialog({ o, onClose, onMade }: { o: Overview; onClose: () => void; onMade: () => Promise<unknown> }) {
  const { busy, run } = useSave();
  const [name, setName] = useState('');
  const [access, setAccess] = useState<Access>('read');
  const [all, setAll] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [ends, setEnds] = useState(false);
  const [until, setUntil] = useState('');
  const [made, setMade] = useState<string | null>(null);
  const ready = name.trim() && (all || picked.length) && (!ends || until > o.today);
  if (made) {
    return (
      <Dialog
        title={t('dev.key_made', { name })}
        onClose={onClose}
        footer={
          <Button variant="primary" onClick={onClose}>
            {t('dev.key_saved')}
          </Button>
        }
      >
        <div className="form-stack">
          <p className="callout callout-warn">
            <Icon name="lock" size={18} />
            <span>{t('dev.key_once')}</span>
          </p>
          <div className="dev-secret">
            <code>{made}</code>
            <CopyButton text={made} />
          </div>
          <p className="muted small">{t('dev.key_keep')}</p>
        </div>
      </Dialog>
    );
  }
  return (
    <Dialog
      title={t('dev.new_key')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            icon="key"
            busy={busy}
            disabled={!ready}
            onClick={() =>
              void run(async () => {
                const r = await action<{ secret: string }>('developer.create_key', { name, access, modules: all ? null : picked, expires_on: ends ? until : null });
                setMade(r.secret);
                await onMade();
              })
            }
          >
            {t('dev.make_key')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <Field label={t('dev.key_name')} hint={t('dev.key_name_hint')} required>
          <input className="input" value={name} maxLength={80} placeholder={t('dev.key_name_ph')} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        <fieldset className="dev-fieldset">
          <legend className="field-label">{t('dev.access')}</legend>
          <div className="dev-choice">
            {(['read', 'write'] as const).map((a) => (
              <label key={a} className={`dev-choice-card${access === a ? ' on' : ''}`}>
                <input type="radio" name="dev-access" checked={access === a} onChange={() => setAccess(a)} />
                <Icon name={a === 'read' ? 'eye' : 'edit'} size={18} />
                <span>
                  <strong>{a === 'read' ? t('dev.read_only') : t('dev.read_write')}</strong>
                  <span className="muted small">{a === 'read' ? t('dev.read_only_hint') : t('dev.read_write_hint')}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
        <PartsPicker modules={o.modules} all={all} setAll={setAll} picked={picked} setPicked={setPicked} />
        <div className="setting-row">
          <div>
            <strong>{t('dev.ends')}</strong>
            <p className="muted">{t('dev.ends_hint')}</p>
          </div>
          <Switch checked={ends} onChange={setEnds} label={t('dev.ends')} />
        </div>
        {ends ? (
          <Field label={t('dev.ends_on')} error={until && until <= o.today ? t('dev.ends_after_today') : null} required>
            <input className="input" type="date" min={o.today} value={until} onChange={(e) => setUntil(e.target.value)} />
          </Field>
        ) : null}
      </div>
    </Dialog>
  );
}

/** Which parts of ERP PRO a key or an app may use: all the person can, or the ones picked. */
export function PartsPicker({
  modules,
  all,
  setAll,
  picked,
  setPicked,
  hint = t('dev.parts_hint'),
}: {
  modules: { name: string; label: string }[];
  all: boolean;
  setAll: (on: boolean) => void;
  picked: string[];
  setPicked: (p: string[]) => void;
  hint?: string;
}) {
  const [find, setFind] = useState('');
  const mods = useMemo(() => {
    const needle = find.trim().toLowerCase();
    return modules
      .map((m) => ({ ...m, text: moduleName(m.name, m.label) }))
      .filter((m) => !needle || m.text.toLowerCase().includes(needle) || picked.includes(m.name))
      .sort((a, b) => a.text.localeCompare(b.text));
  }, [modules, find, picked]);
  return (
    <fieldset className="dev-fieldset">
      <legend className="field-label">{t('dev.parts')}</legend>
      <div className="setting-row">
        <div>
          <strong>{t('dev.all_parts')}</strong>
          <p className="muted">{t('dev.all_parts_hint')}</p>
        </div>
        <Switch checked={all} onChange={setAll} label={t('dev.all_parts')} />
      </div>
      {!all ? (
        <>
          {modules.length > 12 ? (
            <label className="search">
              <Icon name="search" size={16} />
              <input className="input" value={find} placeholder={t('dev.parts_find')} aria-label={t('dev.parts_find')} onChange={(e) => setFind(e.target.value)} />
            </label>
          ) : null}
          <div className="dev-pick" role="group" aria-label={t('dev.parts')}>
            {mods.map((m) => {
              const on = picked.includes(m.name);
              return (
                <button
                  key={m.name}
                  type="button"
                  aria-pressed={on}
                  className={`bank-col-chip${on ? ' on' : ''}`}
                  onClick={() => setPicked(on ? picked.filter((x) => x !== m.name) : [...picked, m.name])}
                >
                  {on ? <Icon name="check" size={14} /> : null}
                  {m.text}
                </button>
              );
            })}
            {!mods.length ? <span className="muted small">{t('dev.ref_nothing')}</span> : null}
          </div>
          {picked.length ? <p className="small dev-picked">{tn('dev.parts_picked', picked.length)}</p> : null}
        </>
      ) : null}
      <p className="muted small">{hint}</p>
    </fieldset>
  );
}

// ---- webhooks -------------------------------------------------------------------------------------

function hookState(w: HookRow): { tone: Tone; icon: IconName; label: string } {
  if (w.active)
    return w.waiting && w.last_error_at && (!w.last_ok_at || w.last_error_at > w.last_ok_at)
      ? { tone: 'warn', icon: 'alert', label: t('dev.retrying') }
      : { tone: 'ok', icon: 'checkCircle', label: t('dev.active') };
  return w.paused_reason === 'failing' ? { tone: 'danger', icon: 'pause', label: t('dev.paused_failing') } : { tone: 'neutral', icon: 'pause', label: t('dev.paused') };
}

function WebhooksTab({ o, onNew, onEdit, onChange }: { o: Overview; onNew: () => void; onEdit: (w: HookRow) => void; onChange: () => Promise<unknown> }) {
  const { busy, run } = useSave();
  const [drop, setDrop] = useState<HookRow | null>(null);
  const [log, setLog] = useState<HookRow | null>(null);
  const [secret, setSecret] = useState<{ hook: HookRow; secret: string | null } | null>(null);
  const [tested, setTested] = useState<{ id: string; ok: boolean; text: string } | null>(null);
  if (!o.can.webhooks)
    return (
      <section className="panel">
        <p className="callout callout-warn">
          <Icon name="lock" size={18} />
          <span>{t('dev.no_seal')}</span>
        </p>
      </section>
    );
  if (!o.webhooks.length)
    return (
      <section className="panel">
        <EmptyState
          icon="send"
          title={t('dev.no_hooks')}
          body={t('dev.no_hooks_body')}
          action={
            <Button variant="primary" icon="plus" onClick={onNew}>
              {t('dev.new_webhook')}
            </Button>
          }
        />
      </section>
    );
  return (
    <div className="dev-hooks">
      {o.webhooks.map((w) => {
        const st = hookState(w);
        return (
          <section key={w.id} className="panel dev-hook" aria-label={w.name}>
            <div className="dev-hook-head">
              <div className="dev-hook-title">
                <h2 className="dev-h2">{w.name}</h2>
                <StatusBadge tone={st.tone} icon={st.icon} label={st.label} />
              </div>
              <div className="dev-hook-acts">
                <Button
                  size="sm"
                  icon="send"
                  busy={busy && tested?.id === w.id}
                  onClick={() =>
                    void run(async () => {
                      setTested({ id: w.id, ok: false, text: '' });
                      const r = await action<{ ok: boolean; status: number | null; answer: string; ms: number }>('developer.test_webhook', { id: w.id });
                      setTested({ id: w.id, ok: r.ok, text: r.status ? `HTTP ${r.status} · ${r.ms} ms${r.answer ? ` · ${r.answer.slice(0, 120)}` : ''}` : r.answer });
                      await onChange();
                    })
                  }
                >
                  {t('dev.send_test')}
                </Button>
                <Button size="sm" variant="ghost" icon="history" onClick={() => setLog(w)}>
                  {t('dev.deliveries')}
                </Button>
                <IconButton icon="edit" label={t('dev.edit_n', { name: w.name })} onClick={() => onEdit(w)} />
                <IconButton
                  icon="lock"
                  label={t('dev.secret_n', { name: w.name })}
                  onClick={() =>
                    void run(async () => {
                      const r = await action<{ secret: string }>('developer.webhook_secret', { id: w.id });
                      setSecret({ hook: w, secret: r.secret });
                    })
                  }
                />
                <IconButton icon="trash" label={t('dev.delete_n', { name: w.name })} onClick={() => setDrop(w)} />
              </div>
            </div>
            <div className="dev-url">
              <Icon name="link" size={14} />
              <code>{w.url}</code>
            </div>
            {tested?.id === w.id && tested.text ? (
              <p className={`callout ${tested.ok ? 'callout-info' : 'callout-danger'} dev-test-result`} role="status">
                <Icon name={tested.ok ? 'checkCircle' : 'alert'} size={18} />
                <span>
                  <strong>{tested.ok ? t('dev.test_ok') : t('dev.test_failed')}</strong> {tested.text}
                </span>
              </p>
            ) : null}
            {w.paused_reason === 'failing' ? (
              <p className="callout callout-warn">
                <Icon name="pause" size={18} />
                <span>{t('dev.paused_failing_body')}</span>
                <Button
                  size="sm"
                  icon="play"
                  onClick={() =>
                    void run(async () => {
                      await action('developer.save_webhook', { id: w.id, name: w.name, url: w.url, events: w.events, active: true });
                      await onChange();
                    }, t('dev.resumed'))
                  }
                >
                  {t('dev.resume')}
                </Button>
              </p>
            ) : null}
            <div className="dev-hook-body">
              <div className="dev-events">
                {w.events.slice(0, 8).map((e) => (
                  <span key={e} className="dev-chip" title={e}>
                    {eventLabel(e)}
                  </span>
                ))}
                {w.events.length > 8 ? <span className="dev-chip dev-chip-more">+{w.events.length - 8}</span> : null}
              </div>
              <dl className="dev-stats">
                <div>
                  <dt>{t('dev.delivered_day')}</dt>
                  <dd>{w.delivered_day}</dd>
                </div>
                <div>
                  <dt>{t('dev.failed_day')}</dt>
                  <dd className={w.dead_day ? 'neg' : undefined}>{w.dead_day}</dd>
                </div>
                <div>
                  <dt>{t('dev.waiting')}</dt>
                  <dd>{w.waiting}</dd>
                </div>
                <div>
                  <dt>{t('dev.last_ok')}</dt>
                  <dd>{w.last_ok_at ? since(w.last_ok_at) : '—'}</dd>
                </div>
              </dl>
              <div className="dev-inline-switch small">
                <Switch
                  checked={w.active}
                  label={w.active ? t('dev.pause') : t('dev.resume')}
                  onChange={(on) =>
                    void run(
                      async () => {
                        await action('developer.save_webhook', { id: w.id, name: w.name, url: w.url, events: w.events, active: on });
                        await onChange();
                      },
                      on ? t('dev.resumed') : t('dev.paused_done'),
                    )
                  }
                />
                {w.active ? t('dev.sending') : t('dev.not_sending')}
              </div>
            </div>
          </section>
        );
      })}
      {drop ? (
        <Confirm
          title={t('dev.delete_title', { name: drop.name })}
          body={t('dev.delete_body')}
          confirm={t('dev.delete')}
          danger
          busy={busy}
          onClose={() => setDrop(null)}
          onConfirm={() =>
            void run(async () => {
              await action('developer.delete_webhook', { id: drop.id });
              setDrop(null);
              await onChange();
            }, t('dev.deleted'))
          }
        />
      ) : null}
      {log ? <DeliveriesDialog hook={log} onClose={() => setLog(null)} onChange={onChange} /> : null}
      {secret ? <SecretDialog name={secret.hook.name} id={secret.hook.id} secret={secret.secret} onClose={() => setSecret(null)} /> : null}
    </div>
  );
}

function SecretDialog({ name, id, secret: first, made, onClose }: { name: string; id: string; secret: string | null; made?: boolean; onClose: () => void }) {
  const { busy, run } = useSave();
  const [secret, setSecret] = useState(first);
  const [roll, setRoll] = useState(false);
  return (
    <Dialog
      title={made ? t('dev.hook_made', { name }) : t('dev.secret_title', { name })}
      onClose={onClose}
      footer={
        <>
          {!made ? (
            <Button variant="ghost" icon="repeat" onClick={() => setRoll(true)}>
              {t('dev.roll')}
            </Button>
          ) : null}
          <Button variant="primary" onClick={onClose}>
            {t('dev.done')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <p className="muted">{t('dev.secret_lead')}</p>
        <div className="dev-secret">
          <code>{secret}</code>
          {secret ? <CopyButton text={secret} /> : null}
        </div>
        <p className="muted small">{t('dev.secret_keep')}</p>
      </div>
      {roll ? (
        <Confirm
          title={t('dev.roll_title')}
          body={t('dev.roll_body')}
          confirm={t('dev.roll')}
          busy={busy}
          onClose={() => setRoll(false)}
          onConfirm={() =>
            void run(async () => {
              const r = await action<{ secret: string }>('developer.webhook_secret', { id, roll: true });
              setSecret(r.secret);
              setRoll(false);
            }, t('dev.rolled'))
          }
        />
      ) : null}
    </Dialog>
  );
}

function HookDialog({ o, hook, onClose, onSaved }: { o: Overview; hook: HookRow | null; onClose: () => void; onSaved: () => Promise<unknown> }) {
  const { busy, run } = useSave();
  const [name, setName] = useState(hook?.name ?? '');
  const [url, setUrl] = useState(hook?.url ?? 'https://');
  const [events, setEvents] = useState<string[]>(hook?.events ?? []);
  const [made, setMade] = useState<{ id: string; secret: string } | null>(null);
  const urlProblem = useMemo(() => {
    if (!url || url === 'https://') return null;
    try {
      const u = new URL(url);
      return u.protocol === 'https:' ? null : t('dev.url_https');
    } catch {
      return t('dev.url_full');
    }
  }, [url]);
  const groups = useMemo(() => {
    const m = new Map<string, EventDef[]>();
    for (const e of o.events) m.set(e.module, [...(m.get(e.module) ?? []), e]);
    return [...m.entries()].sort((a, b) => moduleName(a[0]).localeCompare(moduleName(b[0])));
  }, [o.events]);
  if (made) return <SecretDialog name={name} id={made.id} secret={made.secret} made onClose={onClose} />;
  const toggle = (e: string) => setEvents(events.includes(e) ? events.filter((x) => x !== e) : [...events, e]);
  return (
    <Dialog
      title={hook ? t('dev.edit_n', { name: hook.name }) : t('dev.new_webhook')}
      size="lg"
      onClose={onClose}
      footer={
        <>
          <span className="muted small dev-foot-count">{tn('dev.events_chosen', events.length)}</span>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={!name.trim() || !url || url === 'https://' || !!urlProblem || !events.length}
            onClick={() =>
              void run(
                async () => {
                  const r = await action<{ webhook: { id: string }; secret?: string }>('developer.save_webhook', { ...(hook ? { id: hook.id } : {}), name, url, events });
                  await onSaved();
                  if (r.secret) setMade({ id: r.webhook.id, secret: r.secret });
                  else onClose();
                },
                hook ? t('dev.saved') : undefined,
              )
            }
          >
            {hook ? t('common.save') : t('dev.make_webhook')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <div className="dev-two">
          <Field label={t('dev.hook_name')} hint={t('dev.hook_name_hint')} required>
            <input className="input" value={name} maxLength={80} placeholder={t('dev.hook_name_ph')} onChange={(e) => setName(e.target.value)} autoFocus={!hook} />
          </Field>
          <Field label={t('dev.hook_url')} hint={t('dev.hook_url_hint')} error={urlProblem} required>
            <input className="input" type="url" inputMode="url" value={url} maxLength={500} placeholder="https://example.com/erp-events" onChange={(e) => setUrl(e.target.value.trim())} />
          </Field>
        </div>
        <fieldset className="dev-fieldset">
          <legend className="field-label">{t('dev.events')}</legend>
          <p className="muted small">{t('dev.events_hint')}</p>
          <div className="dev-event-groups">
            {groups.map(([mod, list]) => {
              const allOn = list.every((e) => events.includes(e.event));
              return (
                <div key={mod} className="dev-event-group">
                  <div className="dev-event-group-head">
                    <strong>{moduleName(mod)}</strong>
                    <button
                      type="button"
                      className="link-button small"
                      onClick={() => setEvents(allOn ? events.filter((x) => !list.some((e) => e.event === x)) : [...new Set([...events, ...list.map((e) => e.event)])])}
                    >
                      {allOn ? t('dev.none') : t('dev.all')}
                    </button>
                  </div>
                  {list.map((e) => (
                    <label key={e.event} className="dev-check">
                      <input type="checkbox" checked={events.includes(e.event)} onChange={() => toggle(e.event)} />
                      <span>
                        {eventLabel(e.event)}
                        <code className="muted small">{e.event}</code>
                      </span>
                    </label>
                  ))}
                </div>
              );
            })}
          </div>
        </fieldset>
      </div>
    </Dialog>
  );
}

const DELIVERY_STATE: Record<Delivery['state'], { tone: Tone; icon: IconName }> = {
  pending: { tone: 'warn', icon: 'clock' },
  delivered: { tone: 'ok', icon: 'checkCircle' },
  dead: { tone: 'danger', icon: 'x' },
};

function DeliveriesDialog({ hook, onClose, onChange }: { hook: HookRow; onClose: () => void; onChange: () => Promise<unknown> }) {
  const [filter, setFilter] = useState<'all' | Delivery['state']>('all');
  const { data, reload } = useAction<Delivery[]>('developer.deliveries', { webhook: hook.id, ...(filter === 'all' ? {} : { state: filter }) });
  const { run } = useSave();
  const [open, setOpen] = useState<string | null>(null);
  return (
    <Dialog title={t('dev.deliveries_of', { name: hook.name })} size="lg" onClose={onClose}>
      <div className="segmented subtle" role="tablist" aria-label={t('dev.deliveries')}>
        {(['all', 'delivered', 'pending', 'dead'] as const).map((f) => (
          <button key={f} role="tab" aria-selected={filter === f} className={filter === f ? 'active' : ''} onClick={() => setFilter(f)}>
            {t(`dev.f_${f}` as MessageKey)}
          </button>
        ))}
      </div>
      {!data ? (
        <Skeleton h={200} />
      ) : !data.length ? (
        <EmptyState icon="inbox" title={t('dev.no_deliveries')} body={t('dev.no_deliveries_body')} />
      ) : (
        <ul className="dev-deliveries">
          {data.map((d) => {
            const s = DELIVERY_STATE[d.state];
            const expanded = open === d.id;
            return (
              <li key={d.id} className={`dev-delivery${expanded ? ' open' : ''}`}>
                <button type="button" className="dev-delivery-row" aria-expanded={expanded} onClick={() => setOpen(expanded ? null : d.id)}>
                  <StatusBadge tone={s.tone} icon={s.icon} label={t(`dev.d_${d.state}` as MessageKey)} />
                  <span className="dev-delivery-ev">
                    <strong>{d.event === 'developer.ping' ? t('dev.test') : eventLabel(d.event)}</strong>
                    <span className="muted small">
                      {dateTime(d.created_at)}
                      {d.attempts > 1 ? ` · ${tn('dev.attempts', d.attempts)}` : ''}
                      {d.state === 'pending' && d.attempts ? ` · ${t('dev.next_try', { at: dateTime(d.next_at) })}` : ''}
                    </span>
                  </span>
                  <span className="dev-delivery-http muted small">{d.last_status ? `HTTP ${d.last_status}` : d.attempts ? t('dev.no_answer') : ''}</span>
                  <Icon name="chevronRight" size={16} />
                </button>
                {expanded ? (
                  <div className="dev-delivery-more">
                    {d.last_answer ? (
                      <>
                        <h4 className="dev-h4">{t('dev.answer')}</h4>
                        <Code copy={false}>{d.last_answer}</Code>
                      </>
                    ) : null}
                    <h4 className="dev-h4">{t('dev.sent_data')}</h4>
                    <Code>{JSON.stringify(d.payload, null, 2)}</Code>
                    {d.state === 'dead' && d.event !== 'developer.ping' ? (
                      <Button
                        size="sm"
                        icon="repeat"
                        onClick={() =>
                          void run(async () => {
                            await action('developer.resend', { id: d.id });
                            await Promise.all([reload(), onChange()]);
                          }, t('dev.resend_done'))
                        }
                      >
                        {t('dev.resend')}
                      </Button>
                    ) : null}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </Dialog>
  );
}

// ---- the reference --------------------------------------------------------------------------------

type Picked = { kind: 'model'; name: string } | { kind: 'action'; name: string } | { kind: 'events' };

const schemaType = (s: JsonSchema | undefined): string => {
  if (!s) return 'any';
  if (s.anyOf)
    return s.anyOf
      .map(schemaType)
      .filter((x, i, a) => a.indexOf(x) === i)
      .join(' | ');
  if (s.enum) return s.enum.map((v) => JSON.stringify(v)).join(' | ');
  const type = Array.isArray(s.type) ? s.type.join(' | ') : (s.type ?? 'any');
  if (type === 'array') return `${schemaType(s.items)}[]`;
  return s.format ? `${type} (${s.format})` : type;
};

const sampleOf = (s: JsonSchema | undefined, depth = 0): unknown => {
  if (!s || depth > 3) return null;
  if (s.default !== undefined) return s.default;
  if (s.anyOf) return sampleOf(s.anyOf.find((x) => x.type !== 'null') ?? s.anyOf[0], depth);
  if (s.enum) return s.enum[0];
  const type = Array.isArray(s.type) ? s.type.find((x) => x !== 'null') : s.type;
  switch (type) {
    case 'object':
      return Object.fromEntries(
        Object.entries(s.properties ?? {})
          .filter(([k]) => s.required?.includes(k))
          .map(([k, v]) => [k, sampleOf(v, depth + 1)]),
      );
    case 'array':
      return [sampleOf(s.items, depth + 1)];
    case 'integer':
    case 'number':
      return s.minimum ?? 1;
    case 'boolean':
      return true;
    case 'string':
      return s.format === 'uuid' ? '00000000-0000-0000-0000-000000000000' : s.format === 'date' ? '2026-01-31' : s.format === 'date-time' ? '2026-01-31T09:00:00Z' : 'text';
    default:
      return null;
  }
};

function ReferenceTab({ slug }: { slug: string }) {
  const { data } = useAction<Reference>('developer.reference');
  const { run } = useSave();
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<Picked | null>(null);
  const [mod, setMod] = useState('');
  const isPhone = typeof window !== 'undefined' && window.matchMedia?.('(max-width: 720px)').matches;
  useEffect(() => {
    if (data && !picked && !isPhone) setPicked(data.models[0] ? { kind: 'model', name: data.models[0].name } : data.actions[0] ? { kind: 'action', name: data.actions[0].name } : { kind: 'events' });
  }, [data, picked, isPhone]);
  if (!data) return <Skeleton h={420} r="var(--radius-lg)" />;
  const needle = q.trim().toLowerCase();
  const match = (name: string, label = '') => !needle || name.toLowerCase().includes(needle) || label.toLowerCase().includes(needle);
  const models = data.models.filter((m) => (!mod || m.module === mod) && match(m.name, m.label));
  const actions = data.actions.filter((a) => (!mod || a.module === mod) && match(a.name, a.needs ?? ''));
  const model = picked?.kind === 'model' ? data.models.find((m) => m.name === picked.name) : undefined;
  const act = picked?.kind === 'action' ? data.actions.find((a) => a.name === picked.name) : undefined;
  const key = `erpk.${slug}.…`;
  const base = origin();
  return (
    <div className={`dev-ref${picked && isPhone ? ' detail' : ''}`}>
      <aside className="panel dev-ref-list" aria-label={t('dev.ref_list')}>
        <div className="dev-ref-tools">
          <label className="search">
            <Icon name="search" size={16} />
            <input className="input" value={q} placeholder={t('dev.ref_search')} aria-label={t('dev.ref_search')} onChange={(e) => setQ(e.target.value)} />
          </label>
          <select className="input" value={mod} aria-label={t('dev.ref_module')} onChange={(e) => setMod(e.target.value)}>
            <option value="">{t('dev.ref_all_modules')}</option>
            {data.modules.map((m) => (
              <option key={m.name} value={m.name}>
                {moduleName(m.name, m.label)}
              </option>
            ))}
          </select>
          <Button size="sm" variant="ghost" icon="download" onClick={() => void run(() => download('/api/openapi.json'))}>
            {t('dev.openapi')}
          </Button>
        </div>
        <nav className="dev-ref-nav">
          <h3 className="dev-h4">{tn('dev.ref_records', models.length)}</h3>
          {models.map((m) => (
            <button key={m.name} type="button" className={picked?.kind === 'model' && picked.name === m.name ? 'active' : ''} onClick={() => setPicked({ kind: 'model', name: m.name })}>
              <code>{m.name}</code>
              <span className="muted small">{m.label}</span>
            </button>
          ))}
          <h3 className="dev-h4">{tn('dev.ref_actions', actions.length)}</h3>
          {actions.map((a) => (
            <button key={a.name} type="button" className={picked?.kind === 'action' && picked.name === a.name ? 'active' : ''} onClick={() => setPicked({ kind: 'action', name: a.name })}>
              <code>{a.name}</code>
              {a.needs ? <span className="muted small">{a.needs}</span> : null}
            </button>
          ))}
          {!mod && !needle ? (
            <>
              <h3 className="dev-h4">{t('dev.events')}</h3>
              <button type="button" className={picked?.kind === 'events' ? 'active' : ''} onClick={() => setPicked({ kind: 'events' })}>
                <span>{tn('dev.ref_events', data.events.length)}</span>
              </button>
            </>
          ) : null}
          {!models.length && !actions.length ? <p className="muted small dev-ref-empty">{t('dev.ref_nothing')}</p> : null}
        </nav>
      </aside>
      <section className="panel dev-ref-detail" aria-live="polite">
        {isPhone && picked ? (
          <button type="button" className="link-button dev-back" onClick={() => setPicked(null)}>
            <Icon name="back" size={16} /> {t('dev.ref_back')}
          </button>
        ) : null}
        {model ? (
          <>
            <h2 className="dev-ref-title">
              <code>{model.name}</code>
            </h2>
            <p className="muted">
              {model.label} · {moduleName(model.module)}
            </p>
            <div className="dev-endpoints">
              <Endpoint method="GET" path={`/api/records/${model.name}`} what={t('dev.ep_list')} />
              <Endpoint method="GET" path={`/api/records/${model.name}/{id}`} what={t('dev.ep_get')} />
              {model.write ? (
                <>
                  <Endpoint method="POST" path={`/api/records/${model.name}`} what={t('dev.ep_create')} />
                  <Endpoint method="PATCH" path={`/api/records/${model.name}/{id}`} what={t('dev.ep_update')} />
                  <Endpoint method="DELETE" path={`/api/records/${model.name}/{id}`} what={t('dev.ep_delete')} />
                </>
              ) : null}
            </div>
            <p className="muted small">{model.write ? t('dev.records_write_note') : t('dev.records_read_note')}</p>
            <h3 className="dev-h4">{t('dev.fields')}</h3>
            <Table
              head={[t('dev.field'), t('dev.type'), t('dev.required')]}
              left={[1]}
              rows={model.fields.map((f) => ({
                key: f.name,
                cells: [
                  <code>{f.name}</code>,
                  <span className="small">{f.values ? f.values.map((v) => JSON.stringify(v)).join(' | ') : f.ref ? `${f.type} → ${f.ref}` : f.type}</span>,
                  f.required ? t('dev.yes') : '',
                ],
              }))}
            />
            <h3 className="dev-h4">{t('dev.example')}</h3>
            <Code>{`curl "${base}/api/records/${model.name}?limit=20&order_by=created_at&desc=true" \\
  -H "Authorization: Bearer ${key}"`}</Code>
          </>
        ) : act ? (
          <>
            <h2 className="dev-ref-title">
              <code>{act.name}</code>
            </h2>
            <p className="muted">
              {moduleName(act.module)}
              {act.needs ? ` · ${t('dev.needs', { what: act.needs })}` : ''}
            </p>
            <div className="dev-endpoints">
              <Endpoint method="POST" path={`/api/${act.upload ? 'upload' : 'actions'}/${act.name}`} what={act.upload ? t('dev.ep_upload') : t('dev.ep_action')} />
            </div>
            <h3 className="dev-h4">{t('dev.input')}</h3>
            {Object.keys(act.input.properties ?? {}).length ? (
              <Table
                head={[t('dev.field'), t('dev.type'), t('dev.required')]}
                left={[1]}
                rows={Object.entries(act.input.properties ?? {}).map(([k, v]) => ({
                  key: k,
                  cells: [<code>{k}</code>, <span className="small">{schemaType(v)}</span>, act.input.required?.includes(k) ? t('dev.yes') : ''],
                }))}
              />
            ) : (
              <p className="muted small">{t('dev.no_input')}</p>
            )}
            <h3 className="dev-h4">{t('dev.example')}</h3>
            <Code>{`curl -X POST ${base}/api/${act.upload ? 'upload' : 'actions'}/${act.name} \\
  -H "Authorization: Bearer ${key}" \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify(sampleOf(act.input) ?? {})}'`}</Code>
          </>
        ) : picked?.kind === 'events' ? (
          <>
            <h2 className="dev-ref-title">{t('dev.events')}</h2>
            <p className="muted">{t('dev.events_ref_lead')}</p>
            <Table
              head={[t('dev.event'), t('dev.about'), '']}
              left={[1]}
              phoneHide={[2]}
              rows={data.events.map((e) => ({ key: e.event, cells: [<code>{e.event}</code>, eventLabel(e.event), <code className="muted small">{`${e.id} → ${e.model}`}</code>] }))}
            />
          </>
        ) : (
          <EmptyState icon="book" title={t('dev.ref_pick')} body={t('dev.ref_pick_body')} />
        )}
      </section>
    </div>
  );
}

function Endpoint({ method, path, what }: { method: string; path: string; what: string }) {
  return (
    <div className="dev-endpoint">
      <span className={`dev-method dev-method-${method.toLowerCase()}`}>{method}</span>
      <code>{path}</code>
      <span className="muted small">{what}</span>
    </div>
  );
}
